import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { __t } from '../../../../../i18n/t'
import { DataState, DiscountKind, replacements, selIds } from '../../model'
import { useWiz } from '../../state/WizardContext'
import { Ask, Callout, Rich } from '../../ui/basics'
import { OutlineButton } from '../../ui/controls'
import { MultiPicker } from '../../ui/MultiPicker'
import { C, stackStartSx } from '../../ui/tokens'

const singleStationSx = {
  display: 'flex', alignItems: 'center', gap: 1.5, maxWidth: 520, p: '12px 14px', border: `1px solid ${C.line}`, borderRadius: '12px', bgcolor: C.surface,
} as const
const replaceColumnSx = { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 } as const
const replaceItemSx = { display: 'flex', flexDirection: 'column', gap: 0.25 } as const
const wideCalloutSx = { maxWidth: 760 } as const
const singleTextSx = { display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 } as const
const stationNameSx = { fontSize: 15, fontWeight: 500, overflowWrap: 'anywhere' } as const

/** O Padrão em vigor ainda não chegou ou falhou: o cadastro não afirma nada sobre o que será substituído. */
const DefaultsStatus: React.FC = () => {
  const { lists, d } = useWiz()
  const state = lists.defaults.state
  if (state === DataState.ready) return null
  const blocks = d.kind === DiscountKind.default
  return state === DataState.loading ? (
    <Callout tone="info" role="status" id="f-defaults" sx={wideCalloutSx}>{__t('Conferindo o Padrão atual dos postos…')}</Callout>
  ) : (
    <Callout tone="warn" role="alert" id="f-defaults" sx={wideCalloutSx}>
      <Box sx={stackStartSx}>
        <span>{blocks
          ? __t('Não foi possível conferir o Padrão atual dos postos. Sem isso não dá para saber o que este Padrão vai substituir.')
          : __t('Não foi possível conferir o Padrão atual dos postos. Por isso a escolha entre mostrar as duas opções ou só a promoção não aparece.')}
        </span>
        <OutlineButton onClick={lists.defaults.retry} sx={{ height: 40, px: 1.75, fontSize: 14 }}>{__t('Tentar de novo')}</OutlineButton>
      </Box>
    </Callout>
  )
}

/** "Em quais postos?" + o que acontece com o Padrão atual (só no Padrão). */
export const StationsSection: React.FC = () => {
  const { d, c, set, fe, lists } = useWiz()
  const single = c.stations.length === 1
  const sel = selIds(d, c)
  const items = useMemo(() => c.stations.map(s => ({ id: s.id, name: s.name, meta: s.city })), [c.stations])
  const rep = replacements(d, c)
  const defaultsReady = lists.defaults.state === DataState.ready
  const showNoDefault = d.kind === DiscountKind.default && sel.length > 0 && rep.length === 0 && defaultsReady
  const watchesDefaults = (d.kind === DiscountKind.default || d.kind === DiscountKind.special) && sel.length > 0

  return (
    <Ask id="h-postos" title={__t('Em quais postos?')}>
      {c.stations.length === 0 ? (
        <Callout tone="warn">{__t('Sua rede ainda não tem postos cadastrados. Cadastre um posto para criar descontos.')}</Callout>
      ) : single ? (
        <Box id="f-stations" sx={singleStationSx}>
          <StorefrontIcon aria-hidden sx={{ color: C.muted, flexShrink: 0 }} />
          <Box sx={singleTextSx}>
            <Box component="span" sx={stationNameSx}>{c.stations[0].city ? `${c.stations[0].name} · ${c.stations[0].city}` : c.stations[0].name}</Box>
          </Box>
        </Box>
      ) : (
        <MultiPicker
          id="f-stations"
          kind="stations"
          items={items}
          selected={d.stations}
          onChange={ids => set(x => ({ ...x, stations: ids }))}
          error={fe['f-stations']}
          errorId="f-stations-err"
          footNote={__t('Postos cadastrados depois não entram sozinhos.')}
        />
      )}

      {watchesDefaults ? <DefaultsStatus /> : null}
      <Box role="alert">
        {rep.length ? (
          <Callout tone="warn" role="presentation" sx={wideCalloutSx}>
            <Box sx={replaceColumnSx}>
              <b>{__t('Este desconto vai substituir o Padrão atual')}</b>
              {rep.map(r => (
                <Box key={r.code} sx={replaceItemSx}>
                  <span><Rich text={__t('**%{codigo}** deixa de valer no %{onde}.', { codigo: r.code, onde: r.inSel })} /></span>
                  <Box component="span" sx={{ fontSize: 13 }}>{__t('Hoje o %{codigo} dá, por litro: %{valores}', { codigo: r.code, valores: r.vals })}</Box>
                  {r.hasDropped ? <Box component="span" sx={{ fontSize: 13, fontWeight: 700 }}>{r.droppedText}</Box> : null}
                  {r.hasRest ? <Box component="span" sx={{ fontSize: 13 }}>{__t('No %{rest}, ele continua valendo, com um novo código.', { rest: r.rest })}</Box> : null}
                </Box>
              ))}
            </Box>
          </Callout>
        ) : null}
      </Box>
      {showNoDefault ? (
        <Callout tone="info" role="status" sx={wideCalloutSx}>
          {sel.length > 1 ? __t('Nenhum Padrão ativo nesses postos. Este vai ser o primeiro.') : __t('Nenhum Padrão ativo neste posto. Este vai ser o primeiro.')}
        </Callout>
      ) : null}
    </Ask>
  )
}
